import { StepLink } from '../models/step-link';
import {
    DirectEntryStepEnum,
    DirectEntryStepPaths,
} from '../../direct-entry/shared/constants/direct-entry-steps';
import { RouteConstants } from '../../../../core/constants/routes';
import { FrnFilingStepEnum } from '../../frn-filing/shared/constants/frn-filing-steps';

/**
 * Returns a new array of steps depending on the type of application. Since each application URL may have URLs modified to be
 * application-specific, we need to return a fresh array each time (instead of a constant).
 */
export class ApplicationSteps {
    static getNewApplicationSteps(): StepLink[] {
        return [
            {
                id: 1,
                title: 'Type of Filing',
                url: RouteConstants.Application.getFullRoute('purposeOfFiling'),
            },
            {
                id: 2,
                title: 'Quick Start',
                url: RouteConstants.Application.getFullRoute('quickStart'),
            },
        ];
    }

    static getUnsupportedApplicationSteps(applicationId: string): StepLink[] {
        return [
            {
                id: 1,
                title: 'Contact Request',
                url: RouteConstants.Application.getFullRouteWithApplicationId(
                    'unsupported',
                    applicationId,
                ),
            },
        ];
    }

    static getNewFrnFilingSteps(): StepLink[] {
        return [
            {
                id: FrnFilingStepEnum.FrnType,
                title: 'FRN Type',
                url: RouteConstants.FrnFiling.getFullRoute('newFiling'),
            },
            {
                id: FrnFilingStepEnum.FrnDetail,
                title: 'FRN Detail',
                // This URL will really be used once an applicationId exists
                // but we show it now as a placeholder for the step UI.
                url: RouteConstants.FrnFiling.getFullRoute('detail'),
            },
        ];
    }

    static getFrnFilingSteps(applicationId: string): StepLink[] {
        return [
            {
                id: FrnFilingStepEnum.FrnType,
                title: 'FRN Type',
                url: RouteConstants.FrnFiling.getFullRouteWithApplicationId(
                    'typeOfFiling',
                    applicationId,
                ),
            },
            {
                id: FrnFilingStepEnum.FrnDetail,
                title: 'FRN Detail',
                url: RouteConstants.FrnFiling.getFullRouteWithApplicationId(
                    'detail',
                    applicationId,
                ),
            },
        ];
    }

    static getGoNewApplicationPreparationSteps(): StepLink[] {
        return [
            {
                id: 1,
                title: 'Purpose of Filing',
                url: RouteConstants.Go.getFullRoute('purposeOfFiling'),
            },
            {
                id: 2,
                title: 'Technical Data',
                url: RouteConstants.Go.getFullRoute('parameters'),
            },
            {
                id: 3,
                title: 'Confirm Technical Data',
                url: RouteConstants.Go.getFullRoute('confirmParameters'),
            },
        ];
    }

    static getGoApplicationPreparationSteps(applicationId: string): StepLink[] {
        return [
            {
                id: 1,
                title: 'Technical Data',
                url: RouteConstants.Go.getFullRouteWithApplicationId(
                    'parametersWithApplicationId',
                    applicationId,
                ),
            },
            {
                id: 2,
                title: 'Confirm Technical Data',
                url: RouteConstants.Go.getFullRouteWithApplicationId(
                    'confirmParametersWithApplicationId',
                    applicationId,
                ),
            },
        ];
    }

    static getGoApplicationSteps(applicationId: string): StepLink[] {
        return [
            {
                id: 1,
                title: 'Search Processing',
                url: RouteConstants.Go.getFullRouteWithApplicationId(
                    'searchProcessing',
                    applicationId,
                ),
            },
            {
                id: 2,
                title: 'Frequency Results',
                url: RouteConstants.Go.getFullRouteWithApplicationId(
                    'frequencyResults',
                    applicationId,
                ),
            },
        ];
    }

    /**
     * The complete set of Step Link options for the Direct Entry application type.
     */
    static getDirectEntryStepLinkOptions(applicationId: string): StepLink[] {
        return [
            {
                id: DirectEntryStepEnum.GeneralInfo,
                title: 'General Information',
                sideNavigationTitle: 'General Info',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.generalInfo,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.FeesWaiversExemptions,
                title: 'Fees, Waivers, and Exemptions',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.feesWaiversExemptions,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.ApplicantInfo,
                title: 'Applicant Information',
                sideNavigationTitle: 'Applicant Info',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.applicantInfo,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.RegulatoryInfo,
                title: 'Regulatory Information',
                sideNavigationTitle: 'Regulatory Info',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.regulatoryInfo,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.CertificationInfo,
                title: 'Certification Information',
                sideNavigationTitle: 'Certification Info',
                sideNavigationMaxLength: 18,
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.certificationInfo,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.ScheduleHAdditionalInformation,
                title: 'Schedule H Additional Information',
                sideNavigationTitle: 'Schedule H Step 1',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.scheduleHAdditionalInformation,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.SchedulesDAndH,
                title: 'Schedules D & H',
                sideNavigationTitle: 'Schedule H Step 2',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.schedulesDAndH,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.ScheduleK,
                title: 'Schedule K',
                sideNavigationTitle: 'Schedule K',
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.scheduleK,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.AttachmentsAndWaivers,
                title: 'Attachments and Waivers',
                sideNavigationMaxLength: 15,
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.attachmentsAndWaivers,
                    applicationId,
                ),
            },
            {
                id: DirectEntryStepEnum.SubmitToEwa,
                title: 'Final Step',
                sideNavigationMaxLength: 15,
                url: RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.submitToEwa,
                    applicationId,
                ),
            },
        ];
    }

    static getCoordinationApplicationSteps(applicationId: string): StepLink[] {
        return [
            {
                id: 1,
                title: 'Coordination Parameters',
                sideNavigationTitle: 'Parameters',
                url: RouteConstants.Application.getFullRouteWithApplicationId(
                    'coordinationParameters',
                    applicationId,
                ),
            },
            {
                id: 2,
                title: 'Coordination Results',
                sideNavigationTitle: 'Results',
                url: RouteConstants.Application.getFullRouteWithApplicationId(
                    'coordinationResults',
                    applicationId,
                ),
            },
        ];
    }
}
